import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/inputs/Card';
import { MdClose } from 'react-icons/md';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, FieldArray } from 'formik';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Switch from 'components/inputs/Switch';
import Button from 'components/inputs/Button';
import _ from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';
import axios from 'axios';
import queryString from 'query-string';
import { cookie } from 'utils/cookies/cookies';
import { useDispatch, useSelector } from 'react-redux';
import { CREATE_WHATSNEW, UPDATE_WHATSNEW } from 'actions/whatsNew';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';

const FREQUENCY_OPTIONS = [
  { label: 'Show once (until seen)', value: 'ONCE' },
  { label: 'Once per 24 hours', value: 'ONCE_PER_24H' },
  { label: 'Every app open (until end date)', value: 'EVERY_OPEN' },
];

const TEMPLATE_OPTIONS = [
  { label: 'Template 1 — Simple banner', value: 1 },
  { label: 'Template 2 — Feature highlights', value: 2 },
  { label: 'Template 3 — Ordered / step', value: 3 },
];

const emptySlide = { template: 1, title: '', description: '', image_url: '', bullets: [], cta: { text: '', deep_link: '' }, duration_seconds: '' };

const WhatsNewForm = (props) => {
  const { isEdit, onClose = _.noop() } = props;
  const dispatch = useDispatch();

  const detail = useSelector((state) => state.whatsNew.whatsNewDetail);
  const isLoading = useSelector((state) => state.whatsNew.isLoading);

  // S3 presigned-URL upload (same pattern as FirmWareUpdate)
  const uploadImage = useCallback((acceptedFiles, path, setFieldValue) => {
    const file = acceptedFiles && acceptedFiles[0];
    if (!file) return;
    const token = cookie.get('token');
    axios({
      method: 'get',
      headers: { Authorization: `Bearer ${token}` },
      url: `${process.env.REACT_APP_API_URL}/util/fileupload?name=${file.name}`,
    }).then((res) => {
      const signedRequest = _.get(res, 'data.signedRequest', '');
      if (!signedRequest) return;
      axios({ method: 'put', headers: { 'Content-Type': file.type }, url: signedRequest, data: file }).then((putRes) => {
        if (putRes.status === 200) {
          const imgUrlData = queryString.parseUrl(signedRequest);
          setFieldValue(path, imgUrlData.url);
        }
      });
    });
  }, []);

  const create = useCallback((data) => {
    dispatch({
      type: CREATE_WHATSNEW.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) onClose();
      },
    });
  }, []);

  const update = useCallback((data) => {
    dispatch({
      type: UPDATE_WHATSNEW.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === UPDATE_STATUS_CODE) onClose();
      },
    });
  }, []);

  const schema = Yup.object().shape({
    release_version: Yup.string().required('Release version is required').strict(true).trim('No leading/trailing spaces'),
    slides: Yup.array().min(1, 'Add at least one slide'),
  });

  const initialValues = isEdit
    ? {
        ...detail,
        start_date: detail.start_date ? moment(detail.start_date).format('YYYY-MM-DD') : '',
        end_date: detail.end_date ? moment(detail.end_date).format('YYYY-MM-DD') : '',
        slides: _.get(detail, 'slides', []).map((s) => ({ ...s, bullets: _.get(s, 'bullets', []) })),
      }
    : {
        release_version: '',
        is_active: false,
        start_date: '',
        end_date: '',
        frequency: 'ONCE',
        ring: { label: "What's New", image_url: '' },
        slides: [{ ...emptySlide }],
      };

  // normalise before submit: drop empty strings -> sensible types, add step index for ordered bullets
  const buildPayload = (values) => {
    const payload = _.cloneDeep(values);
    payload.slides = (payload.slides || []).map((slide, idx) => ({
      ...slide,
      order: idx + 1,
      duration_seconds: slide.duration_seconds === '' ? null : Number(slide.duration_seconds),
      // Template 1 (simple banner) has no bullets — drop them even if the admin
      // added some before switching template, so CMS matches what the app shows.
      bullets:
        Number(slide.template) === 1
          ? []
          : (slide.bullets || [])
              .filter((b) => _.trim(_.get(b, 'text', '')))
              .map((b, bIdx) => ({ text: b.text, icon: b.icon || null, step: Number(slide.template) === 3 ? bIdx + 1 : null })),
    }));
    if (!payload.start_date) delete payload.start_date;
    if (!payload.end_date) delete payload.end_date;
    return payload;
  };

  return (
    <div className="faq-form-page__main">
      <Card>
        <div className="faq-form-header__block">
          <div className="faq-header-name">
            <span>{isEdit ? 'Edit' : 'Add'} What&apos;s New</span>
          </div>
          <div className="close___btn">
            <MdClose size={30} color="#be210b" onClick={onClose} />
          </div>
        </div>
        <div className="faq-form-body__block">
          <Formik
            enableReinitialize={!!isEdit}
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
              const payload = buildPayload(values);
              if (isEdit) update(payload);
              else create(payload);
              setSubmitting(false);
            }}
          >
            {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                {/* ---- Release-level config ---- */}
                <Row>
                  <Col lg={4} md={6}>
                    <TextInput
                      isRequired
                      label="Release Version"
                      placeholder="e.g. 2.2.3"
                      name="release_version"
                      value={_.get(values, 'release_version', '')}
                      onChange={handleChange}
                      error={errors.release_version && touched.release_version ? errors.release_version : null}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <TextInput
                      label="Start Date"
                      type="date"
                      name="start_date"
                      value={_.get(values, 'start_date', '')}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <TextInput
                      label="End Date"
                      type="date"
                      name="end_date"
                      value={_.get(values, 'end_date', '')}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <Select
                      label="Frequency"
                      options={FREQUENCY_OPTIONS}
                      value={_.get(values, 'frequency', 'ONCE')}
                      onChange={(val) => setFieldValue('frequency', val)}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <TextInput
                      label="Ring Label"
                      placeholder="What's New"
                      name="ring.label"
                      value={_.get(values, 'ring.label', '')}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col lg={12}>
                    <div className="d-flex align-items-center" style={{ gap: 10, margin: '8px 0' }}>
                      <span>Active</span>
                      <Switch checked={!!_.get(values, 'is_active')} onChange={(checked) => setFieldValue('is_active', checked)} />
                    </div>
                  </Col>
                </Row>

                {/* ---- Slides ---- */}
                <FieldArray name="slides">
                  {({ push, remove }) => (
                    <div className="whatsNew-slides">
                      {_.map(_.get(values, 'slides', []), (slide, index) => (
                        <Card key={`slide-${index}`} className="mb-3" style={{ padding: 12, marginTop: 12 }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>Slide {index + 1}</strong>
                            {values.slides.length > 1 && (
                              <MdClose size={22} color="#be210b" style={{ cursor: 'pointer' }} onClick={() => remove(index)} />
                            )}
                          </div>
                          <Row>
                            <Col lg={4} md={6}>
                              <Select
                                label="Template"
                                options={TEMPLATE_OPTIONS}
                                value={_.get(slide, 'template', 1)}
                                onChange={(val) => setFieldValue(`slides[${index}].template`, val)}
                              />
                            </Col>
                            <Col lg={4} md={6}>
                              <TextInput
                                label="Title"
                                name={`slides[${index}].title`}
                                value={_.get(slide, 'title', '')}
                                onChange={handleChange}
                              />
                            </Col>
                            <Col lg={4} md={6}>
                              <TextInput
                                label="Slide Duration (sec, optional)"
                                type="number"
                                name={`slides[${index}].duration_seconds`}
                                value={_.get(slide, 'duration_seconds', '')}
                                onChange={handleChange}
                              />
                            </Col>
                            <Col lg={12}>
                              <TextInput
                                as="textarea"
                                rows="3"
                                label="Description"
                                name={`slides[${index}].description`}
                                value={_.get(slide, 'description', '')}
                                onChange={handleChange}
                              />
                            </Col>
                            <Col lg={6} md={6}>
                              <TextInput
                                label="CTA Text (optional)"
                                name={`slides[${index}].cta.text`}
                                value={_.get(slide, 'cta.text', '')}
                                onChange={handleChange}
                              />
                            </Col>
                            <Col lg={6} md={6}>
                              <TextInput
                                label="CTA Deep Link (optional)"
                                name={`slides[${index}].cta.deep_link`}
                                value={_.get(slide, 'cta.deep_link', '')}
                                onChange={handleChange}
                              />
                            </Col>
                            <Col lg={12}>
                              <label className="form-label" htmlFor={`slide-image-${index}`}>Image (optional)</label>
                              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                <input
                                  id={`slide-image-${index}`}
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => uploadImage(e.target.files, `slides[${index}].image_url`, setFieldValue)}
                                />
                                {_.get(slide, 'image_url') && (
                                  <img src={_.get(slide, 'image_url')} alt="slide" style={{ height: 40, borderRadius: 4 }} />
                                )}
                              </div>
                            </Col>
                          </Row>

                          {/* bullets — template 2 & 3 only; template 1 (simple banner) has none */}
                          {[2, 3].includes(Number(_.get(slide, 'template'))) && (
                          <FieldArray name={`slides[${index}].bullets`}>
                            {({ push: pushBullet, remove: removeBullet }) => (
                              <div className="whatsNew-bullets" style={{ marginTop: 8 }}>
                                <div className="form-label">Bullet points (template 2 / 3)</div>
                                {_.map(_.get(slide, 'bullets', []), (bullet, bIndex) => (
                                  <Row key={`bullet-${index}-${bIndex}`} className="align-items-center">
                                    <Col lg={10} md={9}>
                                      <TextInput
                                        placeholder={`Point ${bIndex + 1}`}
                                        name={`slides[${index}].bullets[${bIndex}].text`}
                                        value={_.get(bullet, 'text', '')}
                                        onChange={handleChange}
                                      />
                                    </Col>
                                    <Col lg={2} md={3}>
                                      <MdClose size={20} color="#be210b" style={{ cursor: 'pointer' }} onClick={() => removeBullet(bIndex)} />
                                    </Col>
                                  </Row>
                                ))}
                                <Button type="button" className="form_btn" onClick={() => pushBullet({ text: '', icon: '' })}>
                                  + Add bullet
                                </Button>
                              </div>
                            )}
                          </FieldArray>
                          )}
                        </Card>
                      ))}
                      <Button type="button" className="form_btn" onClick={() => push({ ...emptySlide })}>
                        + Add slide
                      </Button>
                    </div>
                  )}
                </FieldArray>

                <div className="form-btn__block" style={{ marginTop: 16 }}>
                  <Button className="form_btn cancel____btn" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting || isLoading} className="form_btn">
                    {isEdit ? 'Update' : 'Add'} What&apos;s New
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

WhatsNewForm.propTypes = {
  onClose: PropTypes.func,
  isEdit: PropTypes.bool,
};

export default WhatsNewForm;
